import { IDatabaseTableModel, IGenericSelectDataModel } from "@kuika/kuika-cl-model";
import * as React from "react";
import TableSvg from "../../../../../svgs/table.svg";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextBold from "../../../../ui-kit/typography/TextBold/TextBold";

export interface ISourcePanelState {
  searchText: string;
}

export interface ISourcePanelProps {
  genericSelectData: IGenericSelectDataModel;
  tables: IDatabaseTableModel[];
  handleSelectTable: (tableSchema: string, tableName: string) => void;
  isDropdownVisible: boolean;
  setDropdownVisibility: (val: boolean) => void;
}

class SourcePanel extends React.PureComponent<ISourcePanelProps, ISourcePanelState> {
  private searchInputRef = React.createRef<HTMLInputElement>();

  constructor(props: ISourcePanelProps) {
    super(props);
    this.state = {
      searchText: ""
    };
  }

  componentDidUpdate(prevProps: ISourcePanelProps) {
    if (!prevProps.isDropdownVisible && this.props.isDropdownVisible) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.searchInputRef.current?.focus();
        }, 50);
      });
    }
  }

  getTableType = (tableName: string): "parent" | "origin" | "child" | null => {
    const { genericSelectData } = this.props;
    const tables = genericSelectData.tables;
    const relations = genericSelectData.relations;

    // Check if this is the origin table (first table)
    if (tables.length > 0 && tables[0].name === tableName) {
      return "origin";
    }

    // Check if this table is a parent (appears in toTable of any relation)
    const isParent = relations.some((rel) => rel.toTable === tableName);
    if (isParent) {
      return "parent";
    }

    // Check if this table is a child (appears in fromTable of any relation, but not as origin)
    const isChild = relations.some((rel) => rel.fromTable === tableName);
    if (isChild) {
      return "child";
    }

    // Check if table is in the selected tables list
    const isInTables = tables.some((t) => t.name === tableName);
    if (isInTables && tables.length > 0 && tables[0].name !== tableName) {
      // It's a selected table but not origin, needs relation check
      return null;
    }

    return null;
  };

  getTableBackgroundColor = (tableName: string): string | undefined => {
    const tableType = this.getTableType(tableName);
    switch (tableType) {
      case "parent":
        return "var(--purple-1)";
      case "origin":
        return "var(--blue-05)";
      case "child":
        return "var(--orange-1)";
      default:
        return undefined;
    }
  };

  sourcesMenu = () => {
    const searchLower = this.state.searchText.toLowerCase();
    const filteredTables = this.props.tables.filter(
      (x) => x.name.toLowerCase().indexOf(searchLower) > -1 || x.schema.toLowerCase().indexOf(searchLower) > -1
    );

    return filteredTables.map((table) => {
      const displayLabel = table.schema ? `${table.schema}.${table.name}` : table.name;
      return {
        key: `${table.schema}.${table.name}`,
        label: displayLabel,
        icon: <TableSvg />,
        onClick: () => this.handleMenuClick(table.schema, table.name),
        style: {
          backgroundColor: this.getTableBackgroundColor(table.name)
        }
      };
    });
  };

  handleMenuClick = (tableSchema: string, tableName: string) => {
    this.props.setDropdownVisibility(false);
    this.props.handleSelectTable(tableSchema, tableName);
  };

  render() {
    const tables = this.props.genericSelectData.tables;

    return (
      <div className="generic-sql__source">
        <KuikaMenuItem
          title={<KuikaTextBold>Source</KuikaTextBold>}
          rightUnit={
            <KuikaDropdownMenu
              options={this.sourcesMenu()}
              open={this.props.isDropdownVisible}
              setOpen={() => this.props.setDropdownVisibility(!this.props.isDropdownVisible)}
              overlayClassName="generic-sql__source-dropdown-overlay"
              placement="bottomLeft"
              header={
                <div className="kuika-flex-between kuika-width-full">
                  <KuikaInput
                    ref={this.searchInputRef}
                    data-testid="searchSourceInput"
                    size="sm"
                    placeholder="Search source..."
                    value={this.state.searchText}
                    onChange={(e) => this.setState({ searchText: e.target.value })}
                    style={{ border: "none" }}
                  />
                  <KuikaButton
                    isIcon
                    type="tertiary"
                    size="sm"
                    color="neutral"
                    onClick={() => this.state.searchText.length > 0 && this.setState({ searchText: "" })}
                  >
                    <KuikaSvg name={this.state.searchText.length > 0 ? "x" : "search"} width={16} height={16} />
                  </KuikaButton>
                </div>
              }
            >
              {(open) => (
                <KuikaButton data-testid="addSourcePlusButton" isIcon type="tertiary" color="neutral" active={open}>
                  <KuikaSvg name={tables.length === 0 ? "plus" : "refresh"} width={16} height={16} />
                </KuikaButton>
              )}
            </KuikaDropdownMenu>
          }
        />
      </div>
    );
  }
}

export const SourcePanelComponent = SourcePanel;
